<script lang="ts" setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { type ArticleCardResponse, getEditorialArticles } from '@/api';
import SectionHeader from '@/components/SectionHeader.vue';
import ArticleList from '@/components/articles/ArticleList.vue';
import { useSsgState } from '@/utilities/ssg-state.ts';

const { editorialCategoryId, editorialLocationId = null } = defineProps<{
  editorialCategoryId: number;
  editorialLocationId?: number | null;
}>();

const { t } = useI18n();

const articles = ref<ArticleCardResponse[] | null>(null);

const loadArticles = async () => {
  const result = await getEditorialArticles({ query: { editorialCategoryId, ...(editorialLocationId !== null ? { editorialLocationId } : {}) } });
  if (!result.data) {
    console.warn(`Related guides could not be loaded. ${JSON.stringify(result.error)}`);
    return;
  }
  articles.value = result.data.items;
};

useSsgState({
  key: `articles:editorial:${editorialCategoryId}:${editorialLocationId ?? 'category'}`,
  refs: { articles },
  isLoaded: () => articles.value !== null,
  load: loadArticles,
});

watch(
  () => [editorialCategoryId, editorialLocationId],
  () => {
    void loadArticles();
  }
);
</script>

<template>
  <section v-if="articles && articles.length > 0">
    <SectionHeader>
      {{ t('views.guides.related.header') }}
      <template #actions>
        <RouterLink :to="{ name: 'guides' }" class="text-ph-green-600 hover:text-ph-green-800 text-sm font-medium whitespace-nowrap">{{ t('views.guides.related.all') }}</RouterLink>
      </template>
    </SectionHeader>
    <ArticleList :articles="articles" />
  </section>
</template>
